import {
  getLogisticsItemKey,
  getLogisticsStageConfig,
  getMockLogisticsStage,
} from "./logisticsStages";
import { getLogisticsMilestoneOptions } from "./logisticsStageUpdateOptions";

const LOGISTICS_TIMELINE = [
  {
    key: "design",
    trackerLabel: "Design",
    cardLabel: "D&E",
    title: "Design & Engineering",
    color: "#E91E63",
    completedNote: "Engineering package approved and released to logistics.",
    currentNote: "Final engineering clarifications are being closed out.",
    pendingNote: "Awaiting engineering kickoff and document release.",
  },
  {
    key: "procurement",
    trackerLabel: "Procure",
    cardLabel: "P",
    title: "Procurement",
    color: "#2196F3",
    completedNote: "Core materials and long-lead items have been secured.",
    currentNote: "Purchase orders and supplier confirmations are in progress.",
    pendingNote: "Starts once design approvals have been completed.",
  },
  {
    key: "fabrication",
    trackerLabel: "Mfg & Fab",
    cardLabel: "M&F",
    title: "Manufacturing & Fabrication",
    color: "#9E9E9E",
    completedNote: "Fabrication packs were completed and released for shipment.",
    currentNote: "Fabrication work packs are active and shop completion is underway.",
    pendingNote: "Queued once procurement is fully closed out.",
  },
  {
    key: "installation",
    trackerLabel: "Install",
    cardLabel: "I",
    title: "Installation",
    color: "#FF9800",
    completedNote: "Installation was completed and handed over for testing.",
    currentNote: "Site installation sequencing is active with field crews mobilized.",
    pendingNote: "Will begin after fabrication and delivery milestones are met.",
  },
  {
    key: "commissioning",
    trackerLabel: "Commission",
    cardLabel: "C",
    title: "Commissioning",
    color: "#4CAF50",
    completedNote: "Commissioning checks were completed and accepted.",
    currentNote: "Commissioning checklists and energization steps are in progress.",
    pendingNote: "Pending installation sign-off and pre-start checks.",
  },
];

const STAGE_PROGRESS_MAP = {
  NOT_STARTED: {
    currentIndex: -1,
    completedCount: 0,
  },
  "DESIGN & ENGINEERING": {
    currentIndex: 0,
    completedCount: 0,
  },
  PROCUREMENT: {
    currentIndex: 1,
    completedCount: 1,
  },
  "MANUFACTURING & FABRICATION": {
    currentIndex: 2,
    completedCount: 2,
  },
  INSTALLATION: {
    currentIndex: 3,
    completedCount: 3,
  },
  COMMISSIONING: {
    currentIndex: 4,
    completedCount: 4,
  },
};

const STATUS_LABELS = {
  completed: "Completed",
  "in-progress": "In Progress",
  pending: "Pending",
};

const DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function hashValue(input) {
  return String(input)
    .split("")
    .reduce((sum, char) => sum + char.charCodeAt(0), 0);
}

function formatLogisticsDate(seed, stageIndex) {
  const offsetDays = 18 + (seed % 9) + stageIndex * (6 + (seed % 3));
  const timestamp =
    Date.UTC(2026, 0, 6) + offsetDays * 24 * 60 * 60 * 1000;
  return DATE_FORMATTER.format(new Date(timestamp));
}

function getStageStatus(stageIndex, currentIndex, completedCount) {
  if (stageIndex < completedCount) return "completed";
  if (stageIndex === currentIndex) return "in-progress";
  return "pending";
}

// True once `milestone` is the LAST milestone in `stageCode`'s own
// milestone list (getLogisticsMilestoneOptions) — i.e. the asset is
// effectively done with its current stage even though the backend hasn't
// yet flipped `logisticsStage` to the next one. Positional, not a
// progressPercent === 100 check: Commissioning's sole milestone is 25%.
function isFinalMilestoneForStage(stageCode, milestone) {
  if (!milestone) return false;
  const options = getLogisticsMilestoneOptions(stageCode);
  if (!options.length) return false;
  return options[options.length - 1].value === milestone;
}

function getProgressValue(seed, stageIndex, status) {
  if (status === "completed") return 100;
  if (status === "pending") return 0;

  return 45 + ((seed + stageIndex * 11) % 4) * 10;
}

function getItemProgressPercent(item) {
  const value =
    item?.logisticsProgressPercent ??
    item?.progressPercent ??
    item?.progress_percent;

  if (!Number.isFinite(Number(value))) return 0;

  return Math.min(100, Math.max(0, Number(value)));
}

function getItemUpdatedDate(item) {
  const value =
    item?.logisticsUpdatedAt ||
    item?.updatedAt ||
    item?.updated_at ||
    item?.modified ||
    "";

  if (!value) return "";

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : DATE_FORMATTER.format(date);
}

function getDisplayName(item) {
  if (!item) return "";

  return (
    item.tagNumber ||
    item.name ||
    item.equipmentName ||
    item.tierName ||
    item.componentName ||
    item.code ||
    ""
  );
}

export function getLogisticsProgressData(item) {
  const stageCode = getMockLogisticsStage(item);
  const stageConfig = getLogisticsStageConfig(stageCode);
  const itemKey = getLogisticsItemKey(item).trim();
  const displayName = getDisplayName(item);
  const seed = itemKey ? hashValue(itemKey) : 0;
  const itemProgressPercent = getItemProgressPercent(item);
  const itemUpdatedDate = getItemUpdatedDate(item);
  const itemMilestone = item?.logisticsMilestone || item?.milestone || "";
  const progressMeta =
    STAGE_PROGRESS_MAP[stageCode] || STAGE_PROGRESS_MAP.NOT_STARTED;

  const stages = LOGISTICS_TIMELINE.map((stage, index) => {
    const status = getStageStatus(
      index,
      progressMeta.currentIndex,
      progressMeta.completedCount,
    );
    const progress =
      status === "in-progress"
        ? itemProgressPercent
        : getProgressValue(seed, index, status);

    return {
      ...stage,
      status,
      statusLabel: STATUS_LABELS[status],
      progress,
      updatedDate:
        status === "pending"
          ? "Awaiting prior stage"
          : itemUpdatedDate || formatLogisticsDate(seed, index),
      notes:
        status === "completed"
          ? stage.completedNote
          : status === "in-progress"
            ? stage.currentNote
            : stage.pendingNote,
      milestone:
        status === "in-progress"
          ? itemMilestone || "Milestone not captured"
          : "",
      isCurrent: index === progressMeta.currentIndex,
      isFinalMilestoneReached:
        status === "in-progress" &&
        isFinalMilestoneForStage(stageCode, itemMilestone),
    };
  });

  return {
    displayName,
    stageCode,
    stageConfig,
    stages,
  };
}

